<script setup>
import { computed, ref, watch } from 'vue'
import { formatMoney } from '@/lib/format.js'

/**
 * A contract's items to correct (D-176) — at a stage's acceptance (as delivered) or in a change
 * of a service's fee: quantity, unit price and value, each correction with its comment. The value
 * follows the quantity and the price (keeping the item's discount), and can be typed over.
 * Emits the corrections only: what differs, or carries a comment.
 */
const props = defineProps({ items: { type: Array, required: true }, currency: { type: String, default: 'PLN' }, priceTbd: Boolean })
const emit = defineEmits(['update:corrections', 'update:complete'])
const rows = ref([])
watch(() => props.items, (items) => {
  rows.value = items.map((i) => ({ uuid: i.uuid, name: i.name, tbd: !!i.tbd, was: { quantity: i.quantity, unit_price: i.unit_price, total: i.total }, quantity: i.quantity, unit_price: i.unit_price, total: i.total, comment: '' }))
}, { immediate: true })

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v))
// What the item's value is to its quantity times price: its discount, kept when either changes.
function factor(r) {
  const { quantity: q, unit_price: p, total: t } = r.was
  return q && p && t ? t / (q * p) : 1
}
function recompute(r) {
  const q = num(r.quantity)
  const p = num(r.unit_price)
  if (q !== null && p !== null) r.total = Math.round(q * p * factor(r) * 100) / 100
}
const differs = (r) => ['quantity', 'unit_price', 'total'].some((k) => num(r[k]) !== num(r.was[k]))
const corrections = computed(() => rows.value.filter((r) => differs(r) || r.comment.trim()).map((r) => ({ uuid: r.uuid, quantity: num(r.quantity), unit_price: num(r.unit_price), total: num(r.total), comment: r.comment.trim() || null })))
watch(corrections, (v) => emit('update:corrections', v), { immediate: true, deep: true })
// Asked to (at an acceptance): what was "to be agreed" has its amount now.
// Every item has its amount at an acceptance — what was "to be agreed" (or a text in place of a price) priced now.
const missing = (r) => props.priceTbd && num(r.total) === null
const complete = computed(() => !rows.value.some(missing))
watch(complete, (v) => emit('update:complete', v), { immediate: true })
const delta = computed(() => rows.value.reduce((sum, r) => sum + ((num(r.total) ?? 0) - (num(r.was.total) ?? 0)), 0))
</script>

<template>
  <div class="max-h-[50vh] overflow-auto rounded-lg border border-default" data-testid="items-editor">
    <table class="w-full text-sm">
      <thead class="sticky top-0 bg-[var(--surface-2)] text-xs text-muted">
        <tr><th class="px-2 py-1 text-left">{{ $t('contract.item') }}</th><th class="w-20 px-1 py-1 text-right">{{ $t('contract.quantity') }}</th><th class="w-28 px-1 py-1 text-right">{{ $t('contract.unitPrice') }}</th><th class="w-28 px-1 py-1 text-right">{{ $t('contract.value') }}</th></tr>
      </thead>
      <tbody>
        <template v-for="r in rows" :key="r.uuid">
          <tr :class="['border-t border-default', differs(r) ? 'bg-amber-50 dark:bg-amber-950/30' : '']">
            <td class="px-2 py-1">{{ r.name }} <span v-if="r.tbd" :class="['ml-1 rounded px-1 text-3xs font-semibold uppercase', missing(r) ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200']">{{ $t('offers.toBeAgreed') }}</span></td>
            <td class="px-1 py-1"><input v-model="r.quantity" type="number" step="any" min="0" class="input py-0.5 text-right text-sm" :data-testid="`fix-qty-${r.name}`" @input="recompute(r)" /></td>
            <td class="px-1 py-1"><input v-model="r.unit_price" type="number" step="0.01" class="input py-0.5 text-right text-sm" @input="recompute(r)" /></td>
            <td class="px-1 py-1"><input v-model="r.total" type="number" step="0.01" :class="['input py-0.5 text-right text-sm', missing(r) ? 'border-red-400 ring-1 ring-red-300' : '']" :data-missing="missing(r) || undefined" :data-testid="`fix-total-${r.name}`" /></td>
          </tr>
          <tr v-if="differs(r) || r.comment" :class="differs(r) ? 'bg-amber-50 dark:bg-amber-950/30' : ''">
            <td colspan="4" class="px-2 pb-1.5">
              <input v-model="r.comment" class="input py-0.5 text-xs" :placeholder="$t('contract.fixComment')" maxlength="1000" :data-testid="`fix-comment-${r.name}`" />
              <p v-if="differs(r)" class="mt-0.5 text-2xs text-muted">{{ $t('contract.was', { q: r.was.quantity ?? '—', p: formatMoney(r.was.unit_price, currency), t: formatMoney(r.was.total, currency) }) }}</p>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <p v-if="!complete" class="sticky bottom-0 border-t border-default bg-red-50 px-2 py-1 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-200" data-testid="items-unpriced">{{ $t('contract.tbdToPrice') }}</p>
    <p v-if="corrections.length" class="sticky bottom-0 border-t border-default bg-[var(--surface-2)] px-2 py-1 text-right text-xs">{{ $t('contract.fixSummary', { n: corrections.length, delta: (delta > 0 ? '+' : '') + formatMoney(delta, currency) }) }}</p>
  </div>
</template>
